
import { Link } from "@tanstack/react-router";
import { api, NowPanelHead, nowRangeQuery, nowRangeWords, type NowRange, useSeenQuery } from "@signet/web-kit";
import { mediaSrc } from "./lib/paths.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Top creators on the Now page (Jay, 2026-10-07), for the range Now is set
   to. Top links was its own panel beside it until 2026-10-28, when Ben had
   them folded into one card.

   Drawn only with Signet's kit classes (the ranking rows are the kit's): a
   links stylesheet imported here would be applied to every page
   (tests/look.test.ts).
   ────────────────────────────────────────────────────────────────────────── */

type Top = {
  from: string;
  to: string;
  creators: {
    id: string;
    name: string;
    image: string | null;
    clicks: number;
    top: { id: string; address: string; source: string | null; clicks: number } | null;
  }[];
};

function useNowTop(range: NowRange) {
  return useSeenQuery({
    queryKey: ["links", "now-top", nowRangeQuery(range)],
    queryFn: () => api<Top>(`/api/m/links/now-top?${nowRangeQuery(range)}`),
    refetchInterval: 60_000,
  });
}

const NOBODY = "Nobody has clicked a link yet.";

/**
 * Top creators, each row carrying the link that brought her the most clicks
 * and where that traffic came from: "IG MAIN: hiiii.me/jackieseefood (5,400)".
 * Ben, 2026-10-28: Top creators and Top links were two panels saying half each,
 * so they became one card across the full width of Now.
 */
export function TopCreatorsPanel({ range }: { range: NowRange }) {
  const top = useNowTop(range);
  const data = top.data;
  const words = nowRangeWords(range);
  const peak = Math.max(1, data?.creators[0]?.clicks ?? 0);
  return (
    <section className="panel">
      <NowPanelHead
        label="Best performing"
        title="Top creators"
        sub={`Clicks ${words} across every link they own, and the link most of them came from.`}
        side={
          <>
            <Link to={"/links/analytics" as never} className="text-button tiny accent">All figures</Link>
            <Link to={"/links/creators" as never} className="text-button tiny accent">All creators</Link>
          </>
        }
      />
      <div className="panel-body">
        {top.isError ? <p className="faint">Couldn't load the top creators.</p> : null}
        {data && !data.creators.length ? <p className="faint">{NOBODY}</p> : null}
        {data && data.creators.length ? (
          <div className="ranking">
            {data.creators.map((creator, index) => (
              <Link
                key={creator.id}
                to={"/links" as never}
                search={{ creator: creator.id } as never}
                className="ranking-row signet-top-creator"
                style={{ animationDelay: `${index * 40}ms` }}
              >
                <span className="ranking-rank">{index + 1}</span>
                <span className="ranking-face">{creator.image ? <img src={mediaSrc(creator.image, 170)} alt="" /> : null}</span>
                <span className="ranking-name"><strong className="truncate">{creator.name}</strong></span>
                {/* Source, link and its clicks each in a column of their own, so every row lines up (Ben, 2026-10-29). */}
                <span className="signet-top-creator-source">
                  {creator.top?.source ? <span className="truncate">{creator.top.source}</span> : null}
                </span>
                <span className="signet-top-creator-link">
                  {creator.top ? (
                    <>
                      <span className="truncate">{creator.top.address}</span>
                      <span className="signet-rank-aside tabular">{creator.top.clicks.toLocaleString()}</span>
                    </>
                  ) : null}
                </span>
                <span className="ranking-bar"><i style={{ width: `${Math.max(3, (creator.clicks / peak) * 100)}%` }} /></span>
                <b className="ranking-value">
                  {creator.clicks.toLocaleString()}
                  <span className="signet-rank-aside"> {creator.clicks === 1 ? "click" : "clicks"}</span>
                </b>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
